import * as THREE from 'three';
import { Scene } from './Scene';
import { palette, PlanetarySky } from '../visual';
import type { SkyBody } from '../visual';
import { Astronaut } from '../gameplay/astronaut';
import { PlayerShip } from '../gameplay/ships';
import {
  Terrain,
  scatterSurfaceRocks,
  tintForPlanet,
  OreDeposit,
  CaelisMiniBase
} from '../gameplay/surface';
import type { SurfaceObstacle } from '../gameplay/surface';
import {
  getWorldResourcePool,
  rollResourceEntry
} from '../gameplay/sector';
import { getItem } from '../gameplay/inventory';
import { CombatSystem, Explosions } from '../gameplay/combat';
import {
  getBiomeProfile,
  scatterFlora,
  spawnFauna,
  Water,
  Weather
} from '../gameplay/biome';
import type { BiomeProfile } from '../gameplay/biome';
import type { InputManager } from '../input';
import type { AudioManager } from '../audio';
import type { FactionSelection } from '../gameplay/factions';
import type { PlayerStats } from '../gameplay/player';

export interface SurfacePalette {
  readonly base: number;
  readonly accent: number;
  readonly detail: number;
  readonly halo: number;
  readonly clouds: number;
}

export interface SurfaceSkyConfig {
  readonly sun?: SkyBody;
  readonly bodies?: readonly SkyBody[];
}

export interface PlanetSurfaceSceneOptions {
  readonly input: InputManager;
  readonly camera: THREE.Camera;
  readonly audio: AudioManager;
  readonly faction: FactionSelection;
  readonly stats: PlayerStats;
}

export interface SurfaceSetup {
  readonly palette: SurfacePalette;
  readonly name: string;
  readonly sky?: SurfaceSkyConfig;
}

// Visible terrain extent. Half-extent (TERRAIN_SIZE/2) needs to satisfy
// (TERRAIN_SIZE/2 - PLANET_BARRIER_RADIUS) > FOG_FAR so the actual mesh
// edge is *always* fully fogged from any point inside the barrier — the
// player never glimpses the "world ends here" line.
const TERRAIN_SIZE = 10000;
const TERRAIN_SEGMENTS = 600;
const TERRAIN_DISPLACEMENT = 32;
const FOG_NEAR = 1200;
const FOG_FAR = 2500;
const ROCK_COUNT = 880;
// Sky dome radius — kept well beyond visible terrain so the dome never
// shows up as a "wall" through the fog.
const SKY_RADIUS = 13000;
/** Soft circular boundary the player can't cross. The controllers clamp
 *  the player back inside and rotate them to face inward when they hit
 *  it. Sized so that explorable area = π·R² ≈ 2× the original 2800×2800
 *  square, and the gap to the visible terrain edge (TERRAIN_SIZE/2 − R)
 *  exceeds FOG_FAR so the edge is invisible. */
export const PLANET_BARRIER_RADIUS = 2400;

export class PlanetSurfaceScene extends Scene {
  readonly id = 'planet-surface';

  private astronaut: Astronaut | null = null;
  private ship: PlayerShip | null = null;
  private terrain: Terrain | null = null;
  private combat: CombatSystem | null = null;
  private explosions: Explosions | null = null;
  private miniBase: CaelisMiniBase | null = null;
  private readonly _obstacles: SurfaceObstacle[] = [];
  private readonly _deposits: OreDeposit[] = [];
  private readonly shipLandingPos = new THREE.Vector3();
  private readonly shipLandingRotY = 0.35;

  private currentPalette: SurfacePalette | null = null;
  private currentName = '';
  private currentSky: SurfaceSkyConfig | null = null;
  private currentBiome: BiomeProfile | null = null;

  constructor(private readonly options: PlanetSurfaceSceneOptions) {
    super();
  }

  getCombatSystem(): CombatSystem | null {
    return this.combat;
  }

  /** Active explosions system for the current planet — used by the FPS
   *  mining tool to spawn hit sparks and destruction puffs. */
  getExplosions(): Explosions | null {
    return this.explosions;
  }

  getStats(): PlayerStats {
    return this.options.stats;
  }

  get terrainInstance(): Terrain {
    if (!this.terrain) throw new Error('Terrain not initialized');
    return this.terrain;
  }

  get obstacles(): readonly SurfaceObstacle[] {
    return this._obstacles;
  }

  /** Water level (Y) of the current biome's pool, or null if dry. */
  getWaterLevel(): number | null {
    return this.currentBiome?.water?.level ?? null;
  }

  /** Pool radius (XZ) of the current biome's water. Null if dry or
   *  the water spans the full disc. */
  getWaterRadius(): number | null {
    return this.currentBiome?.water?.poolRadius ?? null;
  }

  /** Returns true when the supplied head Y is below the active water
   *  surface AND the supplied XZ is inside the pool. Used by the game
   *  director to drive the underwater overlay and oxygen drain. */
  isHeadUnderwater(x: number, z: number, headY: number): boolean {
    const water = this.currentBiome?.water;
    if (!water) return false;
    if (headY >= water.level) return false;
    if (water.poolRadius !== undefined) {
      const distSq = x * x + z * z;
      if (distSq > water.poolRadius * water.poolRadius) return false;
    }
    return true;
  }

  /** Live ore deposits on this planet — drained as the player mines them.
   *  Empty until prepareFor has spawned them; cleared by resetScene. */
  get oreDeposits(): readonly OreDeposit[] {
    return this._deposits;
  }

  /** Called after a deposit is destroyed so its obstacle entry is removed
   *  too. Splice is fine at our deposit count (≤30). */
  removeDeposit(deposit: OreDeposit): void {
    const depIdx = this._deposits.indexOf(deposit);
    if (depIdx >= 0) this._deposits.splice(depIdx, 1);
    const obIdx = this._obstacles.indexOf(deposit.obstacle);
    if (obIdx >= 0) this._obstacles.splice(obIdx, 1);
  }

  get astronautEntity(): Astronaut {
    if (!this.astronaut) throw new Error('Astronaut not initialized');
    return this.astronaut;
  }

  get shipEntity(): PlayerShip {
    if (!this.ship) throw new Error('Ship not initialized');
    return this.ship;
  }

  getPlanetName(): string {
    return this.currentName;
  }

  async prepareFor(setup: SurfaceSetup): Promise<void> {
    this.resetScene();
    this.currentPalette = setup.palette;
    this.currentName = setup.name;
    this.currentSky = setup.sky ?? null;
    this.currentBiome = getBiomeProfile(setup.name);

    this.setupAtmosphere();
    this.setupSky();
    this.setupLighting();
    this.buildTerrain();
    // Water + collision-floor go in BEFORE the ship/astronaut spawn so
    // their landing positions sit on the lake surface instead of the
    // submerged mesh in worlds with large pools (Verdia, Solenne, Nyara).
    this.buildBiomeWater();
    this.buildRocks();
    this.buildOreDeposits();
    this.spawnShip();
    this.spawnAstronaut();
    this.setupCombat();
    this.buildBiomeLifeAndWeather();
    this.spawnPlanetLandmarks();
    this.systems.init();
  }

  /** Drop any planet-specific permanent props after the rest of the
   *  scene is built. Currently spawns the Caelis tutorial mini base —
   *  every other world is unaffected. */
  private spawnPlanetLandmarks(): void {
    this.miniBase = null;
    if (this.currentName !== 'Caelis' || !this.terrain) return;
    // Fixed offset relative to the ship's landing pose so the mini
    // base shows up at the same place on every Caelis visit. Slightly
    // farther than the previous tutorial beacon so the player has to
    // walk a bit, per design.
    const offset = 92;
    const x = this.shipLandingPos.x + offset;
    const z = this.shipLandingPos.z;
    const y = this.terrain.getCollisionGroundY(x, z);
    const base = new CaelisMiniBase(new THREE.Vector3(x, y, z));
    this.root.add(base.object3D);
    this._obstacles.push({
      position: base.object3D.position.clone(),
      radius: 3.4
    });
    this.miniBase = base;
  }

  /** Caelis tutorial mini base — null on every other planet and
   *  before the surface scene has been prepared. The director queries
   *  this for proximity prompts and minimap landmarks. */
  getMiniBase(): CaelisMiniBase | null {
    return this.miniBase;
  }

  private resetScene(): void {
    this.systems.dispose();
    this.entities.clear();
    if (this.miniBase) {
      this.miniBase.dispose();
      this.miniBase = null;
    }
    while (this.root.children.length > 0) {
      const child = this.root.children[this.root.children.length - 1];
      if (!child) break;
      this.root.remove(child);
      disposeObject(child);
    }
    this._obstacles.length = 0;
    this._deposits.length = 0;
    this.astronaut = null;
    this.ship = null;
    this.terrain = null;
    this.combat = null;
    this.explosions = null;
    this.currentBiome = null;
    this.root.background = null;
    this.root.fog = null;
  }

  private setupCombat(): void {
    if (!this.ship) return;
    const explosions = new Explosions(this.root);
    this.explosions = explosions;
    this.systems.register(explosions);
    this.combat = new CombatSystem(
      this.ship,
      this.options.input,
      this.entities,
      this.root,
      this.options.camera,
      explosions,
      undefined,
      this.options.audio
    );
    this.combat.setEnabled(false);
    this.systems.register(this.combat);
  }

  placeAstronautNearShip(): void {
    if (!this.astronaut || !this.terrain || !this.ship) return;
    const forwardOffset = 6;
    const ship = this.ship;
    const shipPos = ship.object3D.position;
    const shipForward = new THREE.Vector3(0, 0, -1).applyEuler(
      ship.object3D.rotation
    );
    const spawnX = shipPos.x + shipForward.x * forwardOffset;
    const spawnZ = shipPos.z + shipForward.z * forwardOffset;
    const groundY = this.terrain.getCollisionGroundY(spawnX, spawnZ);
    this.astronaut.object3D.position.set(spawnX, groundY, spawnZ);
    this.astronaut.object3D.rotation.set(
      0,
      Math.atan2(shipForward.x, shipForward.z),
      0
    );
    this.astronaut.velocity.set(0, 0, 0);
    this.astronaut.isGrounded = true;
  }

  resetShipToLanded(): void {
    if (!this.ship || !this.terrain) return;
    const y =
      this.terrain.getCollisionGroundY(this.shipLandingPos.x, this.shipLandingPos.z) +
      1.4;
    this.ship.object3D.position.set(
      this.shipLandingPos.x,
      y,
      this.shipLandingPos.z
    );
    this.ship.object3D.rotation.set(0, this.shipLandingRotY, 0);
    this.ship.velocity.set(0, 0, 0);
  }

  landShipAtCurrentPosition(): void {
    if (!this.ship || !this.terrain) return;
    const pos = this.ship.object3D.position;
    const groundY = this.terrain.getCollisionGroundY(pos.x, pos.z);
    pos.y = groundY + 1.4;
    this.ship.object3D.rotation.x = 0;
    this.ship.object3D.rotation.z = 0;
    this.ship.velocity.set(0, 0, 0);
  }

  private setupAtmosphere(): void {
    if (!this.currentPalette) return;
    const { clouds, base } = this.currentPalette;
    const defaultFog = mixHex(clouds, base, 0.55);
    const fogColor = this.currentBiome?.fogColor ?? defaultFog;
    this.root.fog = new THREE.Fog(fogColor, FOG_NEAR, FOG_FAR);
  }

  private setupSky(): void {
    if (!this.currentPalette) return;
    const { clouds, base, halo } = this.currentPalette;
    const horizonColor = mixHex(clouds, base, 0.55);
    const zenithColor = mixHex(palette.space.deep, halo, 0.18);
    const atmosphereTint = mixHex(horizonColor, halo, 0.35);

    const sky = new PlanetarySky({
      zenithColor,
      horizonColor,
      atmosphereTint,
      radius: SKY_RADIUS,
      starCount: 1800,
      sun: this.currentSky?.sun,
      bodies: this.currentSky?.bodies
    });
    this.root.add(sky.object3D);
  }

  private setupLighting(): void {
    if (!this.currentPalette) return;
    const { halo, base, accent } = this.currentPalette;

    const ambientWarm =
      this.currentBiome?.ambientWarm ?? mixHex(halo, 0xffffff, 0.25);
    const ambientCool =
      this.currentBiome?.ambientCool ?? mixHex(base, 0x000000, 0.35);
    const ambient = new THREE.HemisphereLight(ambientWarm, ambientCool, 0.55);

    const sunDir = this.currentSky?.sun?.direction;
    const sunPos = sunDir
      ? sunDir.clone().normalize().multiplyScalar(220)
      : new THREE.Vector3(120, 160, 80);
    const sun = new THREE.DirectionalLight(
      mixHex(0xffffff, accent, 0.2),
      1.25
    );
    sun.position.copy(sunPos);

    const rim = new THREE.DirectionalLight(
      mixHex(halo, base, 0.45),
      0.4
    );
    rim.position.set(-sunPos.x * 0.6, 60, -sunPos.z * 0.6);

    const fill = new THREE.DirectionalLight(
      mixHex(accent, 0xffffff, 0.4),
      0.2
    );
    fill.position.set(40, -40, 70);

    this.root.add(ambient, sun, rim, fill);
  }

  private buildTerrain(): void {
    if (!this.currentPalette) return;
    const { base, accent, detail } = this.currentPalette;
    this.terrain = new Terrain({
      size: TERRAIN_SIZE,
      segments: TERRAIN_SEGMENTS,
      baseColor: mixHex(base, 0xffffff, 0.1),
      accentColor: accent,
      detailColor: detail,
      displacement: TERRAIN_DISPLACEMENT
    });
    this.terrain.object3D.frustumCulled = true;
    this.root.add(this.terrain.object3D);
  }

  private buildRocks(): void {
    if (!this.terrain || !this.currentPalette) return;
    const { accent, base, detail } = this.currentPalette;
    const tints = tintForPlanet(accent, base);

    // Spread rocks across the explorable circle plus a small ring of decor
    // beyond the barrier — those outer rocks are mostly fogged but help
    // the horizon read as continuous terrain rather than empty geometry.
    const innerSpread = PLANET_BARRIER_RADIUS * 0.96;
    const outerSpread = PLANET_BARRIER_RADIUS * 1.15;

    const small = scatterSurfaceRocks(this.terrain, {
      count: Math.floor(ROCK_COUNT * 0.75),
      tint: tints.tint,
      accentTint: tints.accentTint,
      minSize: 0.6,
      maxSize: 2.4,
      spread: innerSpread,
      excludeRadius: 18
    });
    enableFrustumCulling(small.group);
    this.root.add(small.group);
    this._obstacles.push(...small.obstacles);

    const large = scatterSurfaceRocks(this.terrain, {
      count: Math.floor(ROCK_COUNT * 0.25),
      tint: mixHex(tints.tint, detail, 0.4),
      accentTint: mixHex(tints.accentTint, detail, 0.35),
      minSize: 3.2,
      maxSize: 7.5,
      spread: outerSpread,
      excludeRadius: 28
    });
    enableFrustumCulling(large.group);
    this.root.add(large.group);
    this._obstacles.push(...large.obstacles);
  }

  private buildOreDeposits(): void {
    if (!this.terrain) return;
    const pool = getWorldResourcePool(this.currentName);
    if (!pool) return;

    // Two-band placement: a tight inner ring guarantees the player sees
    // glowing ore the moment they look around after landing, and an outer
    // band scatters the rest across the explorable disc so exploration
    // still has reach. Inner ring lands ~20-90 units from the ship pad —
    // close enough to walk to immediately, far enough not to clip into it.
    const SHIP_CLEAR_RADIUS = 18;
    const innerRingMin = SHIP_CLEAR_RADIUS;
    const innerRingMax = 90;
    const outerSpread = PLANET_BARRIER_RADIUS * 0.85;

    // ~25% of the world's deposits go into the welcoming ring; the rest
    // spread across the rest of the playable disc.
    const innerCount = Math.max(4, Math.floor(pool.depositCount * 0.25));
    const targetTotal = pool.depositCount;
    let placed = 0;
    let attempts = 0;
    const maxAttempts = targetTotal * 8;
    // Keep deposits 14u apart so they read as discrete nodes rather than
    // a heap, but allow them to sit close enough that two clusters near
    // the pad both feel "right there".
    const MIN_DEPOSIT_GAP_SQ = 14 * 14;

    while (placed < targetTotal && attempts++ < maxAttempts) {
      const inInnerRing = placed < innerCount;
      const angle = Math.random() * Math.PI * 2;
      const r = inInnerRing
        ? innerRingMin + Math.random() * (innerRingMax - innerRingMin)
        : innerRingMax + Math.random() * (outerSpread - innerRingMax);
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;

      // Reject if too close to an existing deposit so they don't bunch up.
      let tooClose = false;
      for (const existing of this._deposits) {
        const dx = existing.object3D.position.x - x;
        const dz = existing.object3D.position.z - z;
        if (dx * dx + dz * dz < MIN_DEPOSIT_GAP_SQ) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) continue;

      const entry = rollResourceEntry(pool);
      if (!entry) continue;
      const definition = getItem(entry.itemId);
      // Larger deposits than before — small ones were getting lost in the
      // ambient surface scatter. 2.4-4.4u reads clearly from 100+ metres.
      const size = 2.4 + Math.random() * 2.0;
      const groundY = this.terrain.getHeightAt(x, z) + size * 0.55;
      const tints = tintForPlanet(
        this.currentPalette?.accent ?? 0xb0b0b0,
        this.currentPalette?.base ?? 0x808080
      );
      const deposit = new OreDeposit({
        position: new THREE.Vector3(x, groundY, z),
        resource: entry,
        definition,
        size,
        tint: tints.tint
      });
      this.entities.add(deposit);
      this._obstacles.push(deposit.obstacle);
      this._deposits.push(deposit);
      placed++;
    }
  }

  /** Build the biome's water surface and apply the matching collision
   *  floor. Runs before the ship/astronaut spawn so they land on the lake
   *  surface instead of clipping through the submerged mesh. */
  private buildBiomeWater(): void {
    if (!this.terrain) return;
    const biome = this.currentBiome;
    if (!biome || !biome.water) {
      this.terrain.setCollisionFloor(null);
      return;
    }
    const playableRadius = PLANET_BARRIER_RADIUS * 0.96;
    const waterRadius = biome.water.poolRadius
      ? Math.min(biome.water.poolRadius, playableRadius)
      : playableRadius * 1.05;
    const water = new Water(biome.water, waterRadius);
    this.systems.register(water);
    this.root.add(water.object3D);
    // Keep the FPS player from sinking below the visible water plane.
    // The visible terrain stays unchanged — only collision is clamped.
    this.terrain.setCollisionFloor(biome.water.level + 0.4, waterRadius);
  }

  /** Lay down flora, fauna and weather — the organic life of the world.
   *  Mineração + asteroides + naves permanecem intactos: este passo só
   *  adiciona habitantes e ambientação ao redor. */
  private buildBiomeLifeAndWeather(): void {
    const biome = this.currentBiome;
    if (!biome || !this.terrain) return;

    const SHIP_CLEAR_RADIUS = 16;
    const playableRadius = PLANET_BARRIER_RADIUS * 0.96;
    const waterLevel = biome.water?.level;

    if (biome.flora.length > 0) {
      const flora = scatterFlora(this.terrain, biome.flora, {
        spread: playableRadius,
        excludeRadius: SHIP_CLEAR_RADIUS,
        // Keep flora out of submerged areas. Distribution defaults to
        // uniform-area across the whole disc.
        minAltitude: waterLevel !== undefined ? waterLevel + 0.4 : undefined
      });
      this.root.add(flora.group);
      this._obstacles.push(...flora.obstacles);
    }

    // Fauna — pull positions from the astronaut entity each tick so the
    // creatures react to the player. Obstacles are appended to the
    // surface obstacle list with their `position` shared by reference,
    // so the FPS controller blocks the player from walking through them
    // and the entries follow the creature as it moves.
    if (biome.fauna.length > 0 && this.astronaut) {
      const astronautPos = this.astronaut.object3D.position;
      const fauna = spawnFauna(this.terrain, biome.fauna, {
        spread: playableRadius,
        excludeRadius: 22,
        getPlayerPosition: () => astronautPos,
        boundaryRadius: PLANET_BARRIER_RADIUS * 0.95,
        stats: this.options.stats,
        playerRadius: this.astronaut.collisionRadius
      });
      for (const c of fauna.creatures) {
        this.entities.add(c);
      }
      this._obstacles.push(...fauna.obstacles);
    }

    // Weather — anchored to the camera so the particle field travels with
    // the player. The camera is the active perspective camera shared by
    // the controllers.
    if (biome.weather) {
      const weather = new Weather(biome.weather, this.options.camera);
      this.systems.register(weather);
      this.root.add(weather.object3D);
    }
  }

  private spawnShip(): void {
    if (!this.terrain) return;
    // If the biome has a water pool centered at the origin, push the
    // landing pad clear of the lake so the ship never lands in the
    // water. The pool is always centred on origin, so a fixed offset
    // along +X by `poolRadius + buffer` lands on dry terrain.
    const pool = this.currentBiome?.water?.poolRadius;
    const buffer = 60;
    const offset = pool ? pool + buffer : 0;
    const x = offset;
    const z = 0;
    const y = this.terrain.getCollisionGroundY(x, z) + 1.4;
    this.shipLandingPos.set(x, y, z);

    this.ship = new PlayerShip({ selection: this.options.faction });
    this.ship.object3D.position.copy(this.shipLandingPos);
    this.ship.object3D.rotation.y = this.shipLandingRotY;
    this.entities.add(this.ship);

    this._obstacles.push({
      position: new THREE.Vector3(this.shipLandingPos.x, 0, this.shipLandingPos.z),
      radius: 2.6,
      height: 3.5
    });
  }

  private spawnAstronaut(): void {
    if (!this.currentPalette) return;
    const { accent, halo } = this.currentPalette;
    this.astronaut = new Astronaut({
      suit: mixHex(palette.ship.hull, 0xffffff, 0.3),
      accent,
      visor: 0x0e0e18,
      dark: 0x18181f,
      glove: 0x14141c,
      clipGlow: mixHex(halo, 0xffffff, 0.35)
    });
    this.astronaut.object3D.position.set(6, 0, 6);
    this.entities.add(this.astronaut);
  }
}

function enableFrustumCulling(root: THREE.Object3D): void {
  root.traverse((obj) => {
    obj.frustumCulled = true;
  });
}

function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.geometry?.dispose?.();
      const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(material)) {
        for (const m of material) m.dispose();
      } else if (material) {
        material.dispose();
      }
    }
  });
}

function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}
